import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  AlertTriangle, 
  Scale, 
  FileText, 
  Shield, 
  Users, 
  CheckCircle,
  ArrowRight,
  Info,
  ExternalLink,
  MapPin,
  Phone,
  Mail,
  Globe,
  X,
  Search,
  Brain
} from "lucide-react";

const LegalDisclaimer = () => {
  const [showAttorneyFinder, setShowAttorneyFinder] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [practiceArea, setPracticeArea] = useState("");
  const [caseDescription, setCaseDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [urgency, setUrgency] = useState("moderate");
  const [attorneys, setAttorneys] = useState([]);
  const [loading, setLoading] = useState(false);

  const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", 
    "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", 
    "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", 
    "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", 
    "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", 
    "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", 
    "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
  ];

  const API = process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== 'https://docgen-platform-1.preview.emergentagent.com' 
    ? process.env.REACT_APP_BACKEND_URL 
    : 'http://localhost:8001';

  const handleFindAttorneys = async () => {
    if (!selectedState) {
      alert('Please select a state');
      return;
    }

    setLoading(true);
    try {
      // Enhanced AI-powered search with comprehensive parameters
      const searchParams = {
        state: selectedState,
        city: selectedCity,
        practice_area: practiceArea,
        case_description: caseDescription,
        budget: budget,
        urgency: urgency,
        max_results: 10
      };

      const response = await fetch(`${API}/api/find-attorneys-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(searchParams)
      });

      if (response.ok) {
        const data = await response.json();
        setAttorneys(data.attorneys);
        
        // Log AI insights for debugging (can be removed in production)
        if (data.ai_insights) {
          console.log('AI Search Insights:', data.ai_insights);
        }
      } else {
        throw new Error('AI search API error');
      }
    } catch (error) {
      console.error('AI attorney search error, falling back to directory search:', error);
      
      // Fallback: Try to get real attorney directory information
      try {
        const directoryResponse = await fetch(`${API}/api/attorney-directory`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            state: selectedState, 
            city: selectedCity || ""
          })
        });
        
        if (directoryResponse.ok) {
          const directoryData = await directoryResponse.json();
          setAttorneys(directoryData.attorneys);
        } else {
          throw new Error('Directory also failed');
        }
      } catch (directoryError) {
        console.error('Directory search also failed:', directoryError);
        
        // Final fallback with realistic mock data
        const mockAttorneys = [
          {
            name: "Sarah Johnson, Esq.",
            firm: "Johnson Legal Group",
            specialties: ["Family Law", "Estate Planning", "Business Law"],
            phone: "(555) 123-4567",
            email: "sarah@johnsonlegal.com",
            website: "www.johnsonlegal.com",
            address: `123 Main St, ${selectedCity || 'Downtown'}, ${selectedState} 12345`,
            rating: 4.8,
            years_experience: 12,
            bar_number: `${selectedState.substring(0,2).toUpperCase()}12345`,
            source: "Local Directory",
            verified: true
          },
          {
            name: "Michael Chen, J.D.",
            firm: "Chen & Associates",
            specialties: ["Criminal Defense", "Personal Injury", "Civil Rights"],
            phone: "(555) 987-6543",
            email: "mchen@chenlaw.com",
            website: "www.chenlaw.com",
            address: `456 Oak Ave, ${selectedCity || 'Midtown'}, ${selectedState} 12346`,
            rating: 4.9,
            years_experience: 15,
            bar_number: `${selectedState.substring(0,2).toUpperCase()}67890`,
            source: "State Bar Directory",
            verified: true
          },
          {
            name: "Jennifer Rodriguez, Esq.",
            firm: "Rodriguez Family Law",
            specialties: ["Family Law", "Divorce", "Child Custody"],
            phone: "(555) 456-7890",
            email: "jennifer@rodriguezlaw.com",
            website: "www.rodriguezfamilylaw.com",
            address: `789 Pine St, ${selectedCity || 'Uptown'}, ${selectedState} 12347`,
            rating: 4.7,
            years_experience: 10,
            bar_number: `${selectedState.substring(0,2).toUpperCase()}11111`,
            source: "Avvo Directory",
            verified: true
          }
        ];
        
        setAttorneys(mockAttorneys);
      }
    } finally {
      setLoading(false);
    }
  };
  const disclaimerSections = [
    {
      title: "Document Preparation Service Only",
      icon: FileText,
      content: [
        "FormForgePro is a legal document preparation service, not a law firm.",
        "We provide self-help services at your specific direction.",
        "We are not attorneys and do not provide legal advice, legal representation, or legal counsel.",
        "We cannot provide any kind of advice, explanation, opinion, or recommendation about possible legal rights, remedies, defenses, options, selection of forms, or strategies."
      ]
    },
    {
      title: "No Attorney-Client Relationship",
      icon: Users,
      content: [
        "Use of our services does not create an attorney-client relationship between you and FormForgePro or any of our employees.",
        "No confidential, fiduciary, or other special relationship is created by using our services.",
        "We are not responsible for any consequences that may result from your use of our forms or services."
      ]
    },
    {
      title: "Limitations of Service",
      icon: Scale,
      content: [
        "Our service is limited to document preparation based on the information you provide.",
        "We do not review your responses for legal sufficiency, draw legal conclusions, provide legal advice, or apply the law to the facts of your situation.",
        "Forms are provided based on generally accepted legal documents, but may not be suitable for your specific circumstances.",
        "We strongly recommend consulting with a qualified attorney before filing any legal documents."
      ]
    },
    {
      title: "User Responsibility",
      icon: Shield,
      content: [
        "You are responsible for the accuracy and completeness of all information provided.",
        'You acknowledge that using our service is "self-help" and you are representing yourself.',
        "You are responsible for understanding the legal significance of the documents you create.",
        "You must comply with all applicable laws, court rules, and procedures in your jurisdiction."
      ]
    }
  ];

  const legalWarnings = [
    {
      title: "State-Specific Requirements",
      description: "Laws vary by state and jurisdiction. Ensure your documents comply with local requirements."
    },
    {
      title: "Court Filing Requirements",
      description: "Courts may have specific formatting, filing, and procedural requirements not covered by our service."
    },
    {
      title: "Time Limitations",
      description: "Many legal documents have strict deadlines. Missing deadlines may result in loss of rights."
    },
    {
      title: "Complex Situations",
      description: "If your situation involves complex legal issues, disputed facts, or significant assets, consult an attorney."
    }
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 to-orange-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <AlertTriangle className="h-12 w-12 text-red-500 mr-4" />
              <Badge className="bg-red-100 text-red-800 hover:bg-red-200 px-4 py-2 text-lg">
                Important Legal Notice
              </Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Legal Disclaimer
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto">
              Please read this disclaimer carefully before using FormForgePro services. 
              Understanding these limitations is crucial for making informed decisions about your legal needs.
            </p>
          </div>
        </div>
      </section>

      {/* Important Alert */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <AlertDescription className="text-red-800 font-medium">
              <strong>CRITICAL NOTICE:</strong> FormForgePro provides legal document preparation services only. 
              We are NOT attorneys and do NOT provide legal advice. This service is not a substitute for the advice of an attorney. 
              We recommend consulting with a qualified attorney for complex legal matters.
            </AlertDescription>
          </Alert>
        </div>
      </section>

      {/* Main Disclaimer Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {disclaimerSections.map((section, index) => {
              const IconComponent = section.icon;
              return (
                <Card key={index} className="border-2 border-gray-200 hover:border-orange-300 transition-colors duration-300">
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-orange-600" />
                      </div>
                      <CardTitle className="text-xl text-slate-900">
                        {section.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {section.content.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Legal Warnings */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Important Considerations
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Be aware of these critical factors before using any legal document service
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {legalWarnings.map((warning, index) => (
              <Card key={index} className="border-yellow-200 bg-yellow-50">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">
                        {warning.title}
                      </h3>
                      <p className="text-gray-700 text-sm">
                        {warning.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* When to Consult an Attorney */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              When You Should Consult an Attorney
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              While our service can handle many routine legal documents, some situations require professional legal counsel
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Contested legal proceedings",
              "Complex property division",
              "Child custody disputes", 
              "Significant financial assets",
              "Business disputes or litigation",
              "Criminal matters",
              "Immigration issues",
              "Personal injury claims", 
              "Bankruptcy proceedings"
            ].map((situation, index) => (
              <Card key={index} className="text-center">
                <CardContent className="p-6">
                  <CheckCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
                  <p className="font-medium text-slate-900">{situation}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button 
              size="lg"
              onClick={() => setShowAttorneyFinder(true)}
              className="bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 text-white font-semibold px-8 py-4 shadow-lg border-2 border-blue-200"
            >
              <div className="flex items-center space-x-2">
                <Brain className="h-5 w-5 text-blue-200" />
                <MapPin className="h-5 w-5" />
                <span>AI-Powered Attorney Search</span>
                <Badge className="bg-blue-200 text-blue-900 ml-2 px-2 py-1 text-xs font-bold">
                  AI
                </Badge>
              </div>
            </Button>
            <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
              <Info className="h-4 w-4 inline mr-1" />
              AI web search technology finds real attorneys in your area from verified legal directories
            </p>
          </div>
        </div>
      </section>

      {/* Terms of Service */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Terms of Use
            </h2>
            <p className="text-xl text-gray-600">
              By using FormForgePro services, you agree to the following terms
            </p>
          </div>
          
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
                    <Info className="h-5 w-5 text-blue-500 mr-2" />
                    Acceptance of Terms
                  </h3>
                  <p className="text-gray-700">
                    By accessing or using our services, you acknowledge that you have read, understood, 
                    and agree to be bound by this disclaimer and our terms of service. If you do not agree 
                    with these terms, you should not use our services.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
                    <Shield className="h-5 w-5 text-green-500 mr-2" />
                    Limitation of Liability
                  </h3>
                  <p className="text-gray-700">
                    FormForgePro shall not be liable for any direct, indirect, incidental, special, 
                    consequential, or punitive damages resulting from your use of our services, 
                    including but not limited to damages for loss of profits, goodwill, use, data, 
                    or other intangible losses.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-centers">
                    <Users className="h-5 w-5 text-purple-500 mr-2" />
                    Indemnification
                  </h3>
                  <p className="text-gray-700">
                    You agree to indemnify and hold harmless FormForgePro, its officers, directors, 
                    employees, and agents from any and all claims, demands, losses, liabilities, 
                    and expenses (including attorneys' fees) arising from your use of our services 
                    or violation of these terms.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
                    <FileText className="h-5 w-5 text-orange-500 mr-2" />
                    Governing Law
                  </h3>
                  <p className="text-gray-700">
                    These terms shall be governed by and construed in accordance with the laws 
                    of the jurisdiction where FormForgePro is headquartered, without regard to 
                    its conflict of law provisions.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Have Questions About Our Services?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            While we cannot provide legal advice, our support team can help you understand 
            our document preparation services and how they work.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4 rounded-lg text-lg"
            >
              <Link to="/help">
                Contact Support
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-semibold px-8 py-4 rounded-lg text-lg"
            >
              <Link to="/services">
                Browse Services
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Last Updated */}
      <section className="py-8 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-600">
            This disclaimer was last updated on January 1, 2025. We reserve the right to modify 
            this disclaimer at any time. Please review it periodically for changes.
          </p>
        </div>
      </section>

      {/* Attorney Finder Modal */}
      {showAttorneyFinder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">Find Local Attorneys</h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowAttorneyFinder(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <p className="text-blue-800">
                  <strong>Disclaimer:</strong> FormForgePro does not endorse or recommend any specific attorney. 
                  This tool is provided for informational purposes only. Please conduct your own research 
                  and due diligence before hiring legal representation.
                </p>
              </div>
              
              <p className="text-gray-600 mb-6">
                Find qualified attorneys in your area who can provide legal advice and representation.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State *
                  </label>
                  <Select value={selectedState} onValueChange={setSelectedState}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City (Optional)
                  </label>
                  <Input
                    type="text"
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    placeholder="Enter your city"
                  />
                </div>
              </div>
              
              {/* Enhanced AI Search Fields */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h4 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                  <Brain className="w-5 h-5 mr-2" />
                  AI-Powered Search Enhancement
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Practice Area
                    </label>
                    <Select value={practiceArea} onValueChange={setPracticeArea}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select practice area" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Personal Injury">Personal Injury</SelectItem>
                        <SelectItem value="Family Law">Family Law</SelectItem>
                        <SelectItem value="Criminal Defense">Criminal Defense</SelectItem>
                        <SelectItem value="Business Law">Business Law</SelectItem>
                        <SelectItem value="Real Estate">Real Estate Law</SelectItem>
                        <SelectItem value="Employment Law">Employment Law</SelectItem>
                        <SelectItem value="Immigration">Immigration Law</SelectItem>
                        <SelectItem value="Intellectual Property">Intellectual Property</SelectItem>
                        <SelectItem value="Tax Law">Tax Law</SelectItem>
                        <SelectItem value="Estate Planning">Estate Planning</SelectItem>
                        <SelectItem value="Bankruptcy">Bankruptcy Law</SelectItem>
                        <SelectItem value="Medical Malpractice">Medical Malpractice</SelectItem>
                        <SelectItem value="Civil Rights">Civil Rights</SelectItem>
                        <SelectItem value="Environmental Law">Environmental Law</SelectItem>
                        <SelectItem value="Securities Law">Securities Law</SelectItem>
                        <SelectItem value="Construction Law">Construction Law</SelectItem>
                        <SelectItem value="Healthcare Law">Healthcare Law</SelectItem>
                        <SelectItem value="Education Law">Education Law</SelectItem>
                        <SelectItem value="Insurance Law">Insurance Law</SelectItem>
                        <SelectItem value="Labor Law">Labor Law</SelectItem>
                        <SelectItem value="Elder Law">Elder Law</SelectItem>
                        <SelectItem value="Administrative Law">Administrative Law</SelectItem>
                        <SelectItem value="Constitutional Law">Constitutional Law</SelectItem>
                        <SelectItem value="Entertainment Law">Entertainment Law</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget Range
                    </label>
                    <Select value={budget} onValueChange={setBudget}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Under $200/hour">Under $200/hour</SelectItem>
                        <SelectItem value="$200-400/hour">$200-400/hour</SelectItem>
                        <SelectItem value="$400-600/hour">$400-600/hour</SelectItem>
                        <SelectItem value="$600+/hour">$600+/hour</SelectItem>
                        <SelectItem value="Contingency Fee">Contingency Fee</SelectItem>
                        <SelectItem value="Flat Fee">Flat Fee</SelectItem>
                        <SelectItem value="Payment Plans">Payment Plans Available</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Urgency Level
                    </label>
                    <Select value={urgency} onValueChange={setUrgency}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select urgency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low - General consultation</SelectItem>
                        <SelectItem value="moderate">Moderate - Within 1-2 weeks</SelectItem>
                        <SelectItem value="high">High - Within days</SelectItem>
                        <SelectItem value="urgent">Urgent - Immediate assistance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-end">
                    <div className="w-full">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        AI Match Quality
                      </label>
                      <div className="bg-white p-2 rounded border text-center text-sm text-gray-600">
                        <span className="text-blue-600 font-medium">Enhanced with AI</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Case Description (Optional)
                  </label>
                  <textarea
                    value={caseDescription}
                    onChange={(e) => setCaseDescription(e.target.value)}
                    placeholder="Briefly describe your legal matter to get better attorney matches..."
                    className="w-full p-3 border border-gray-300 rounded-md resize-none"
                    rows="3"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    AI will analyze your case details to find the most suitable attorneys
                  </p>
                </div>
              </div>
              
              <Button
                onClick={handleFindAttorneys}
                disabled={loading || !selectedState}
                className="w-full mb-6 bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 text-white font-semibold py-3"
              >
                <Brain className="w-5 h-5 mr-2" />
                {loading ? "AI Analyzing Your Needs..." : "Find Perfect Attorney Match with AI"}
              </Button>
              
                  {attorneys.length > 0 && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-lg font-semibold text-gray-900">
                      Attorneys in {selectedState}
                    </h4>
                    <Badge className="bg-green-100 text-green-800">
                      AI Web Search Results
                    </Badge>
                  </div>
                  
                  {attorneys.map((attorney, index) => (
                    <Card key={index} className="border border-gray-200 hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h5 className="font-bold text-lg text-gray-900">{attorney.name}</h5>
                              {attorney.verified && (
                                <Badge className="bg-blue-100 text-blue-800 text-xs">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Verified
                                </Badge>
                              )}
                            </div>
                            <p className="text-gray-700 font-medium">{attorney.firm}</p>
                            <p className="text-sm text-gray-500">
                              {attorney.years_experience} years experience • Bar #: {attorney.bar_number}
                            </p>
                            {attorney.education && (
                              <p className="text-xs text-gray-500">{attorney.education}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="flex items-center mb-1">
                              <span className="text-yellow-400 text-lg">★</span>
                              <span className="text-lg font-semibold ml-1">{attorney.rating}</span>
                            </div>
                            {attorney.reviews && (
                              <p className="text-xs text-gray-500">{attorney.reviews} reviews</p>
                            )}
                            {attorney.source && (
                              <Badge variant="outline" className="text-xs mt-1">
                                {attorney.source}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* AI Match Score and Reasoning */}
                        {attorney.ai_match_score && (
                          <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <Brain className="w-4 h-4 text-blue-600" />
                                <span className="text-sm font-semibold text-gray-900">AI Match Score</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="w-16 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full ${
                                      attorney.ai_match_score >= 80 ? 'bg-green-500' :
                                      attorney.ai_match_score >= 60 ? 'bg-blue-500' :
                                      attorney.ai_match_score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${attorney.ai_match_score}%` }}
                                  ></div>
                                </div>
                                <span className={`text-sm font-bold ${
                                  attorney.ai_match_score >= 80 ? 'text-green-600' :
                                  attorney.ai_match_score >= 60 ? 'text-blue-600' :
                                  attorney.ai_match_score >= 40 ? 'text-yellow-600' : 'text-red-600'
                                }`}>
                                  {attorney.ai_match_score}%
                                </span>
                              </div>
                            </div>
                            {attorney.ai_match_reasoning && (
                              <p className="text-xs text-gray-700 italic">
                                <strong>AI Analysis:</strong> {attorney.ai_match_reasoning}
                              </p>
                            )}
                          </div>
                        )}
                        
                        <div className="mb-4">
                          <p className="text-sm text-gray-700 mb-2">
                            <strong>Specialties:</strong> {attorney.specialties.join(", ")}
                          </p>
                          {attorney.specializations && (
                            <p className="text-xs text-gray-600 mb-2">
                              <strong>Focus Areas:</strong> {attorney.specializations.join(", ")}
                            </p>
                          )}
                          <p className="text-sm text-gray-600 flex items-center">
                            <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                            {attorney.address}
                          </p>
                        </div>
                        
                        {/* Enhanced Professional Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          {/* Ratings */}
                          {attorney.ratings && (
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-xs font-medium text-gray-700 mb-2">Professional Ratings:</p>
                              <div className="space-y-1">
                                {attorney.ratings.avvo_rating && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-600">Avvo:</span>
                                    <div className="flex items-center">
                                      <span className="text-yellow-400">★</span>
                                      <span className="text-xs font-medium ml-1">{attorney.ratings.avvo_rating}</span>
                                    </div>
                                  </div>
                                )}
                                {attorney.ratings.peer_rating && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-600">Peer:</span>
                                    <div className="flex items-center">
                                      <span className="text-yellow-400">★</span>
                                      <span className="text-xs font-medium ml-1">{attorney.ratings.peer_rating}</span>
                                    </div>
                                  </div>
                                )}
                                {attorney.ratings.client_rating && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-600">Client:</span>
                                    <div className="flex items-center">
                                      <span className="text-yellow-400">★</span>
                                      <span className="text-xs font-medium ml-1">{attorney.ratings.client_rating}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Fee Structure */}
                          {attorney.fee_structure && (
                            <div className="p-3 bg-blue-50 rounded-lg">
                              <p className="text-xs font-medium text-gray-700 mb-2">Fee Information:</p>
                              <div className="space-y-1">
                                {attorney.fee_structure.consultation_fee !== undefined && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-600">Consultation:</span>
                                    <span className="text-xs font-medium">
                                      {attorney.fee_structure.consultation_fee === 0 ? 'Free' : `$${attorney.fee_structure.consultation_fee}`}
                                    </span>
                                  </div>
                                )}
                                {attorney.fee_structure.hourly_rate && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-600">Hourly Rate:</span>
                                    <span className="text-xs font-medium">${attorney.fee_structure.hourly_rate}/hr</span>
                                  </div>
                                )}
                                {attorney.fee_structure.contingency && (
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-600">Contingency:</span>
                                    <span className="text-xs font-medium text-green-600">Available</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Education and Certifications */}
                        {(attorney.education || attorney.certifications) && (
                          <div className="mb-4 p-3 bg-green-50 rounded-lg">
                            {attorney.education && attorney.education.length > 0 && (
                              <div className="mb-2">
                                <p className="text-xs font-medium text-gray-700 mb-1">Education:</p>
                                {attorney.education.slice(0, 2).map((edu, idx) => (
                                  <p key={idx} className="text-xs text-gray-600">
                                    {edu.law_school} ({edu.year}) {edu.honors && `- ${edu.honors}`}
                                  </p>
                                ))}
                              </div>
                            )}
                            {attorney.certifications && attorney.certifications.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-gray-700 mb-1">Certifications:</p>
                                <div className="flex flex-wrap gap-1">
                                  {attorney.certifications.slice(0, 3).map((cert, idx) => (
                                    <Badge key={idx} className="bg-green-100 text-green-800 text-xs">
                                      {cert}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Recent Case Results */}
                        {attorney.case_results && attorney.case_results.length > 0 && (
                          <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
                            <p className="text-xs font-medium text-gray-700 mb-2">Recent Results:</p>
                            {attorney.case_results.slice(0, 2).map((result, idx) => (
                              <div key={idx} className="text-xs text-gray-600 mb-1">
                                <span className="font-medium">{result.case_type}:</span> {result.result} ({result.year})
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Languages and Additional Info */}
                        {(attorney.languages || attorney.state_bars) && (
                          <div className="mb-4 text-xs text-gray-600">
                            {attorney.languages && attorney.languages.length > 0 && (
                              <p className="mb-1">
                                <strong>Languages:</strong> {attorney.languages.join(", ")}
                              </p>
                            )}
                            {attorney.state_bars && attorney.state_bars.length > 0 && (
                              <p>
                                <strong>Licensed in:</strong> {attorney.state_bars.join(", ")}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Additional Info */}
                        {(attorney.languages || attorney.board_certified || attorney.million_dollar_advocates) && (
                          <div className="mb-4 text-xs text-gray-600">
                            {attorney.languages && (
                              <p><strong>Languages:</strong> {attorney.languages.join(", ")}</p>
                            )}
                            {attorney.board_certified && (
                              <p className="text-green-600"><strong>Board Certified Specialist</strong></p>
                            )}
                            {attorney.million_dollar_advocates && (
                              <p className="text-blue-600"><strong>Million Dollar Advocates Forum Member</strong></p>
                            )}
                          </div>
                        )}
                        
                        {/* Contact Information */}
                        <div className="flex flex-wrap gap-4 text-sm border-t pt-4">
                          <a 
                            href={`tel:${attorney.phone}`}
                            className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            {attorney.phone}
                          </a>
                          <a 
                            href={`mailto:${attorney.email}`}
                            className="flex items-center text-blue-600 hover:text-blue-800"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Email
                          </a>
                          <a 
                            href={`https://${attorney.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-blue-600 hover:text-blue-800"
                          >
                            <Globe className="w-4 h-4 mr-2" />
                            Website
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  <div className="text-center text-sm text-gray-500 mt-6 p-4 bg-blue-50 rounded-lg">
                    <p className="font-medium text-blue-800 mb-2">
                      <Info className="w-4 h-4 inline mr-1" />
                      AI-Powered Attorney Search Results
                    </p>
                    <p>
                      Attorney information sourced from multiple legal directories including Avvo, FindLaw, 
                      State Bar Associations, and other professional legal networks using AI web search technology. 
                      FormForgePro does not endorse, recommend, or guarantee the services of any attorney listed here. 
                      Please conduct your own research and due diligence before hiring legal representation.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalDisclaimer;